import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppFooter, Brand, HostView, LandingPage, ViewerView } from '../components';
import { readJoinIntent, resolveApiBaseUrl, SessionManager } from '../session';
import { createIceConfiguration } from '../webrtc';

export function App() {
  const joinIntent = useMemo(() => readJoinIntent(window.location), []);
  const manager = useMemo(
    () =>
      new SessionManager({
        apiBaseUrl: resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL, window.location.href),
        iceConfiguration: createIceConfiguration(import.meta.env).configuration,
      }),
    [],
  );
  const [snapshot, setSnapshot] = useState(manager.snapshot);

  useEffect(() => {
    const unsubscribe = manager.subscribe(setSnapshot);
    return () => {
      unsubscribe();
      manager.dispose();
    };
  }, [manager]);

  if (snapshot.phase === 'active' || snapshot.phase === 'ending') {
    return (
      <AppScaffold>
        {snapshot.role === 'host' ? (
          <HostView
            snapshot={snapshot}
            onEnd={() => void manager.end('Your screen-sharing session has ended.')}
            onLock={(locked) => manager.setLocked(locked)}
            onRemoveViewer={() => manager.removeViewer()}
            onRetry={() => manager.retryConnection()}
            onToggleMicrophone={() => void manager.toggleMicrophone()}
          />
        ) : (
          <ViewerView
            snapshot={snapshot}
            fullscreenSupported={manager.fullscreenSupported}
            onLeave={() => void manager.end('You left the session.')}
            onRetry={() => manager.retryConnection()}
          />
        )}
        {snapshot.error ? (
          <div class="session-toast" role="alert">
            <span>{snapshot.error}</span>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => manager.clearError()}
            >
              ×
            </button>
          </div>
        ) : null}
        {snapshot.phase === 'ending' ? (
          <div class="ending-overlay" role="status" aria-live="polite">
            <span class="spinner" aria-hidden="true" />
            Ending session…
          </div>
        ) : null}
      </AppScaffold>
    );
  }

  if (snapshot.phase === 'ended') {
    return (
      <AppScaffold>
        <main class="end-screen">
          <header>
            <Brand />
          </header>
          <section class="end-card" aria-labelledby="end-title">
            <span class="end-card__icon" aria-hidden="true">
              ✓
            </span>
            <p class="eyebrow">Session closed</p>
            <h1 id="end-title">That’s a wrap.</h1>
            <p>{snapshot.endedMessage ?? 'This private session has ended.'}</p>
            <p class="end-card__privacy">
              Chat and connection data have been cleared from this tab.
            </p>
            <button class="primary-action" type="button" onClick={() => manager.reset()}>
              Back to start
            </button>
          </section>
        </main>
      </AppScaffold>
    );
  }

  return (
    <AppScaffold>
      <LandingPage
        busy={snapshot.phase === 'starting'}
        busyRole={snapshot.role}
        captureSupported={manager.captureSupported}
        error={snapshot.error}
        joinIntent={joinIntent}
        onClearError={() => manager.clearError()}
        onHost={(durationSeconds) => void manager.startHosting(durationSeconds)}
        onJoin={(roomCode, pin) =>
          void manager.joinRoom({
            roomCode,
            pin,
            ...(joinIntent?.inviteToken ? { inviteToken: joinIntent.inviteToken } : {}),
          })
        }
      />
    </AppScaffold>
  );
}

function AppScaffold({ children }: { readonly children: ComponentChildren }) {
  return (
    <div class="app-frame">
      {children}
      <AppFooter />
    </div>
  );
}
